import React, { useState, useEffect, useContext } from 'react';
import { 
  Box, 
  Paper, 
  Grid, 
  Typography, 
  TextField, 
  Button, 
  Divider,
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Autocomplete,
  IconButton,
  Card,
  CardContent,
  Alert,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CardHeader,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useOrganizationContext } from '../../../context/organization_context/OrganizationContext';
import { getAllProducts } from '../../../repository/ProductsDAO';
import { useUserContext } from '../../../context/user_context/UserContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { CloudFunctions } from '../../../utils/enums/CloudFunctions';
import { useLoadingContext } from '../../../context/loading/LoadingContext';
import { ToastType, useToast } from '../../../context/toast/ToastContext';
import { StringKeys } from '../../../resources/strings/StringKeys';
import { useTranslation } from 'react-i18next';
import AppSidebar from '../../../components/navbar/DesktopAppSidebar';
import { ProductsContext } from '../../../contexts/ProductsContext';
import { InvoiceContext } from '../../../contexts/InvoiceContext';
import InvoiceProductSelector from './InvoiceProductSelector';
import { formatCurrency, formatDate } from '../../../utils/formatters';

const InvoiceCreationForm = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { products } = useContext(ProductsContext);
    const { organizationCustomers, organizationStripeInfo } = useOrganizationContext()
    const { firebaseUser } = useUserContext();
    const { showToast } = useToast();
    const { setLoadingData } = useLoadingContext();

    // Get today's date + 30 days for default due date
    const getDefaultDueDate = () => {
        const date = new Date();
        date.setDate(date.getDate() + 30);
        return date.toISOString().split('T')[0];
    };

    // Create invoice context value
    const [invoice, setInvoice] = useState({
        customer: null,
        dueDate: getDefaultDueDate(),
        notes: '',
        items: [{ product: null, quantity: 1, price: 0, total: 0 }],
        subtotal: 0,
        tax: 0,
        total: 0,
        automaticTaxEnabled: true
    });
    
    // Provide invoice context value
    const invoiceContextValue = { invoice, setInvoice };

    // Calculate totals whenever items change
    useEffect(() => {
        const subtotal = invoice.items.reduce((sum, item) => 
            sum + (item.price * item.quantity), 0);
        
        // For now, the exact tax amount will be calculated on the server
        // This is just an estimate for display purposes
        // We use 0 since we can't accurately calculate taxes without location information
        const estimatedTax = 0; 
        
        setInvoice(prev => ({
            ...prev,
            subtotal,
            tax: estimatedTax,
            total: subtotal + estimatedTax
        }));
    }, [invoice.items]);

    const handleItemChange = (index, field, value) => {
        setInvoice(prev => {
            const newItems = [...prev.items];
            
            switch (field) {
                case 'product':
                    // Extract the price from the product
                    const productPrice = value ? 
                        (value.price || (value.default_price?.unit_amount ? value.default_price.unit_amount / 100 : 0)) 
                        : 0;
                    
                    newItems[index] = {
                        ...newItems[index],
                        product: value,
                        price: productPrice,
                        // Update total based on new price and current quantity
                        total: productPrice * newItems[index].quantity
                    };
                    break;
                case 'quantity':
                    newItems[index] = {
                        ...newItems[index],
                        quantity: value,
                        // Update total based on current price and new quantity
                        total: newItems[index].price * value
                    };
                    break;
                case 'price':
                    newItems[index] = {
                        ...newItems[index],
                        price: value,
                        // Update total based on new price and current quantity
                        total: value * newItems[index].quantity
                    };
                    break;
                default:
                    newItems[index] = { ...newItems[index], [field]: value };
            }
            
            // Recalculate totals
            return calculateTotals({ ...prev, items: newItems });
        });
    };

    const calculateTotals = (invoiceData) => {
        const subtotal = invoiceData.items.reduce((sum, item) => sum + item.total, 0);
        
        // Tax calculation (if automatic tax is enabled, this will be handled by Stripe)
        const tax = invoiceData.automaticTaxEnabled ? 0 : 0; // We don't calculate tax manually with automatic tax
        
        const total = subtotal + tax;
        
        return {
            ...invoiceData,
            subtotal,
            tax,
            total
        };
    };

    const handleCreateInvoice = async () => {
        if (!invoice.customer) {
            // Handle customer error
            return;
        }
        
        setLoadingData(true);

        try {
            const functions = getFunctions();
            const createInvoice = httpsCallable(functions, CloudFunctions.CreateInvoiceForStripeCustomers);
            
            const result = await createInvoice({
                accountId: organizationStripeInfo.stripe_account_id,
                organizationId: firebaseUser.organization,
                customerId: invoice.customer.stripe_customer_id || invoice.customer.id,
                dueDate: invoice.dueDate,
                customerData: {
                    address: invoice.customer.address,
                    city: invoice.customer.city,
                    state: invoice.customer.state,
                    zip_code: invoice.customer.zip_code,
                    email: invoice.customer.email,
                },
                items: invoice.items
                    .filter(item => item.product !== null)
                    .map(item => {
                        // Get the unit amount either from default_price or calculated from the price
                        const unitAmount = item.product.default_price?.unit_amount || 
                            Math.round(item.price * 100); // Convert dollars to cents for Stripe
                        
                        return {
                            amount: unitAmount * item.quantity,
                            description: item.product.description,
                            price: item.product.default_price?.id,
                            quantity: item.quantity,
                            tax_code: item.product.tax_code || 'txcd_99999999',
                            // If no default_price.id exists, we'll need to create a price
                            unit_amount: !item.product.default_price?.id ? unitAmount : undefined,
                            currency: !item.product.default_price?.id ? 'usd' : undefined,
                            product: !item.product.default_price?.id ? item.product.id : undefined
                        };
                    })
            });

            if (result.data.success) {
                showToast(t(StringKeys.SUCCESSFULLY_CREATED_INVOICE), ToastType.SUCCESS);
                navigate('/invoices');
            } else {
                showToast(result.data.error?.details || t(StringKeys.FAILED_TO_CREATE_INVOICE), ToastType.DANGER);
                console.error('Error creating invoice:', result.data.error);
            }
        } catch (error) {
            showToast(t(StringKeys.FAILED_TO_CREATE_INVOICE), ToastType.DANGER);
            console.error('Error creating invoice:', error);
        } finally {
            setLoadingData(false);
        }
    };

    // Function to handle input focus
    const handleInputFocus = (event) => {
        // Small delay to ensure the element is visible
        setTimeout(() => {
            // Only call select() on text or number input elements
            if (event.target.tagName === 'INPUT' && 
                (event.target.type === 'text' || event.target.type === 'number')) {
                event.target.select();
            }
            
            // Scroll the element into view for all input types
            event.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
    };

    return (
        <InvoiceContext.Provider value={invoiceContextValue}>
            <AppSidebar
                content={
                    <Box sx={{ 
                        p: 3,
                        height: '100%',
                        overflowY: 'auto',
                        WebkitOverflowScrolling: 'touch', // For smooth scrolling on iOS
                    }}>
                        {/* Header */}
                        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h5" component="h1">{t(StringKeys.CREATE_NEW_INVOICE)}</Typography>
                        </Box>

                        <Grid container spacing={3}>
                            {/* Customer Details Section */}
                            <Grid item xs={12} md={6}>
                                <Card sx={{ height: '100%', boxShadow: 5 }}>
                                    <CardContent>
                                        <Typography variant="h6" sx={{ mb: 2 }}>{t(StringKeys.CUSTOMER)}</Typography>
                                        <FormControl fullWidth>
                                            <InputLabel>{t(StringKeys.SELECT_CUSTOMER)}</InputLabel>
                                            <Select
                                                value={invoice.customer?.id || ''}
                                                label={t(StringKeys.SELECT_CUSTOMER)}
                                                onChange={(e) => {
                                                    const selectedCustomer = organizationCustomers.find(c => c.id === e.target.value);
                                                    setInvoice(prev => ({ ...prev, customer: selectedCustomer }));
                                                }}
                                                onFocus={handleInputFocus}
                                            >
                                                <MenuItem value="">
                                                    <em>{t(StringKeys.NONE)}</em>
                                                </MenuItem>
                                                {organizationCustomers.map((customer) => (
                                                    <MenuItem key={customer.id} value={customer.id}>
                                                        {customer.first_name} {customer.last_name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        {invoice.customer && (
                                            <Box sx={{ mt: 2 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {t(StringKeys.EMAIL)}: {invoice.customer.email}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {t(StringKeys.ADDRESS)}: {invoice.customer.address}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {invoice.customer.city}, {invoice.customer.state} {invoice.customer.zip_code}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {t(StringKeys.PHONE)}: {invoice.customer.phone_number}
                                                </Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Invoice Details Section */}
                            <Grid item xs={12} md={6}>
                                <Card sx={{ height: '100%', boxShadow: 5 }}>
                                    <CardContent>
                                        <Typography variant="h6" sx={{ mb: 2 }}>{t(StringKeys.INVOICE_DETAILS)}</Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    type="date"
                                                    label={t(StringKeys.DUE_DATE)}
                                                    InputLabelProps={{ shrink: true }}
                                                    value={invoice.dueDate}
                                                    onChange={(e) => setInvoice(prev => 
                                                        ({ ...prev, dueDate: e.target.value }))}
                                                    onFocus={handleInputFocus}
                                                />
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Line Items Section */}
                            <Grid item xs={12}>
                                <Card sx={{ boxShadow: 5 }}>
                                    <CardContent>
                                        <Typography variant="h6" sx={{ mb: 2 }}>{t(StringKeys.LINE_ITEMS)}</Typography>
                                        <TableContainer sx={{ overflowX: 'auto' }}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ minWidth: 200 }}>{t(StringKeys.PRODUCT)}</TableCell>
                                                        <TableCell sx={{ minWidth: 100 }}>{t(StringKeys.QUANTITY)}</TableCell>
                                                        <TableCell sx={{ minWidth: 100 }}>{t(StringKeys.PRICE)}</TableCell>
                                                        <TableCell sx={{ minWidth: 100 }}>{t(StringKeys.TOTAL)}</TableCell>
                                                        <TableCell sx={{ minWidth: 100 }}>{t(StringKeys.DELETE_ITEM)}</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {invoice.items.map((item, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell>
                                                                <InvoiceProductSelector
                                                                    selectedProduct={item.product}
                                                                    onChange={(product) => handleItemChange(index, 'product', product)}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <TextField
                                                                    type="number"
                                                                    variant="outlined"
                                                                    size="small"
                                                                    value={item.quantity}
                                                                    onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 1)}
                                                                    inputProps={{ min: 1 }}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography>
                                                                    {formatCurrency(item.price)}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography>
                                                                    {formatCurrency(item.total)}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <IconButton 
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={() => {
                                                                        const newItems = invoice.items.filter((_, i) => i !== index);
                                                                        setInvoice(prev => ({ ...prev, items: newItems }));
                                                                    }}
                                                                >
                                                                    <Delete />
                                                                </IconButton>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>

                                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start' }}>
                                            <Button
                                                size="small"
                                                startIcon={<Add />}
                                                onClick={() => setInvoice(prev => ({
                                                    ...prev,
                                                    items: [...prev.items, { product: null, quantity: 1, price: 0, total: 0 }]
                                                }))}
                                            >
                                                {t(StringKeys.ADD_LINE_ITEM)}
                                            </Button>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Summary Section */}
                            <Grid item xs={12}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <Card sx={{ boxShadow: 5, height: '100%' }}>
                                            <CardContent>
                                                <Typography variant="h6" sx={{ mb: 2 }}>
                                                    {t(StringKeys.PROCESSING_FEES)}
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Typography variant="body2">BiziSmart Fee</Typography>
                                                        <Typography variant="body2">2.1%</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Typography variant="body2">Stripe Processing Fee</Typography>
                                                        <Typography variant="body2">2.9% + $0.30</Typography>
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Card sx={{ boxShadow: 5, height: '100%' }}>
                                            <CardContent>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={6}>
                                                        <Typography>{t(StringKeys.SUB_TOTAL)}:</Typography>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography align="right">
                                                            {formatCurrency(invoice.subtotal)}
                                                        </Typography>
                                                    </Grid>
                                                    
                                                    <Grid item xs={6}>
                                                        <Typography>
                                                            {t(StringKeys.TAX)}:
                                                            {invoice.automaticTaxEnabled && 
                                                                <Typography 
                                                                    component="span" 
                                                                    variant="caption" 
                                                                    sx={{ ml: 1, color: 'text.secondary' }}
                                                                >
                                                                    (Calculated at checkout)
                                                                </Typography>
                                                            }
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography align="right">
                                                            {invoice.automaticTaxEnabled 
                                                                ? 'Auto' 
                                                                : `$${formatCurrency(invoice.tax)}`}
                                                        </Typography>
                                                    </Grid>
                                                    
                                                    <Grid item xs={12}>
                                                        <Divider sx={{ my: 1 }} />
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography variant="h6">{t(StringKeys.TOTAL)}:</Typography>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography variant="h6" align="right">
                                                            {formatCurrency(invoice.total)}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* Submit Button Section - Different positioning for mobile/desktop */}
                            <Grid item xs={12}>
                                <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: { xs: 'center', md: 'flex-end' },
                                    mt: { xs: 2, md: 0 },
                                    position: { xs: 'sticky', md: 'static' },
                                    bottom: { xs: '16px', md: 'auto' },
                                    pb: { xs: 2, md: 0 },
                                    backgroundColor: 'background.default',
                                    zIndex: 1,
                                }}>
                                    <Button 
                                        variant="contained" 
                                        onClick={handleCreateInvoice}
                                        sx={{
                                            width: { xs: '100%', md: 'auto' },
                                            maxWidth: { xs: '400px', md: 'none' }
                                        }}
                                    >
                                        {t(StringKeys.SUBMIT)}
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                }
            />
        </InvoiceContext.Provider>
    );
};

export default InvoiceCreationForm;